package com.evoapps.evoshift.ui.adaptive

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.width
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.boundsInWindow
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp

/** Test tags of [PostureAwareScaffold]'s panes (UI tests and robots). */
object PostureTags {
    const val CONTENT = "posture_content"
    const val CONTROLS = "posture_controls"
    const val HINGE = "posture_hinge"
}

/** The narrowest half that gets its own pane; a hinge nearer an edge keeps the ordinary layout. */
val HingePaneMinSide: Dp = 160.dp

/**
 * The layout a [PostureAwareScaffold] chose, for what is inside it: e.g. the roster shows its day
 * in the controls pane instead of a bottom sheet when this is not [PostureLayout.SINGLE].
 */
val LocalPostureLayout = staticCompositionLocalOf { PostureLayout.SINGLE }

/**
 * SH-102: a screen's [content] and its [controls], arranged for the device's posture:
 * - normal windows (phones, tablets, desktop, the web): [normal], the screen's ordinary layout,
 *   unchanged (null: the content above the controls);
 * - half-opened with a horizontal hinge (iPhone Duo or a Pixel Fold like a laptop, tabletop): the
 *   content above the hinge, the controls below it, nothing across it;
 * - half-opened with a vertical hinge (book): the content left of the hinge, the controls right;
 * - opened flat on a nearly square display: the content and the controls side by side.
 *
 * Each pane gets the whole of its half; scroll inside it. Without [controls] (nothing to act on
 * right now) the content takes the whole area: scrolling content may cross the hinge.
 */
@Composable
fun PostureAwareScaffold(
    modifier: Modifier = Modifier,
    controls: (@Composable ColumnScope.() -> Unit)?,
    normal: (@Composable () -> Unit)? = null,
    content: @Composable ColumnScope.() -> Unit
) {
    val ordinary: @Composable () -> Unit = normal ?: {
        Column(modifier.fillMaxSize()) {
            content()
            controls?.invoke(this)
        }
    }
    val posture = LocalDevicePosture.current
    if (posture == DevicePosture.Normal || controls == null) {
        ordinary()
        return
    }
    var area by remember { mutableStateOf<Rect?>(null) }
    BoxWithConstraints(modifier.fillMaxSize().onGloballyPositioned { area = it.boundsInWindow() }) {
        val minSide = with(LocalDensity.current) { HingePaneMinSide.toPx() }
        val layout = postureLayoutFor(posture, maxWidth, maxHeight)
        val split = (posture as? DevicePosture.HalfOpened)?.let { half ->
            area?.let { hingeSplit(it, half.hingeBounds, half.orientation, minSide) }
        }
        // A fold that misses this area (or is not placed yet): the ordinary layout.
        val effective = if ((layout == PostureLayout.TABLETOP || layout == PostureLayout.BOOK) && split == null) PostureLayout.SINGLE else layout
        CompositionLocalProvider(LocalPostureLayout provides effective) {
            val density = LocalDensity.current
            when (effective) {
                PostureLayout.SINGLE -> ordinary()
                PostureLayout.TABLETOP -> Column(Modifier.fillMaxSize()) {
                    Column(Modifier.fillMaxWidth().height(with(density) { split!!.before.toDp() }).testTag(PostureTags.CONTENT), content = content)
                    HingeGap(Modifier.fillMaxWidth().height(with(density) { split!!.hinge.toDp() }))
                    Column(Modifier.fillMaxWidth().weight(1f).testTag(PostureTags.CONTROLS), content = controls)
                }
                PostureLayout.BOOK -> Row(Modifier.fillMaxSize()) {
                    Column(Modifier.fillMaxHeight().width(with(density) { split!!.before.toDp() }).testTag(PostureTags.CONTENT), content = content)
                    HingeGap(Modifier.fillMaxHeight().width(with(density) { split!!.hinge.toDp() }))
                    Column(Modifier.fillMaxHeight().weight(1f).testTag(PostureTags.CONTROLS), content = controls)
                }
                PostureLayout.TWO_PANE -> Row(Modifier.fillMaxSize(), horizontalArrangement = Arrangement.spacedBy(0.dp)) {
                    Column(Modifier.fillMaxHeight().weight(TwoPaneContentWeight).testTag(PostureTags.CONTENT), content = content)
                    Box(Modifier.fillMaxHeight().width(1.dp).background(PaneDivider))
                    Column(Modifier.fillMaxHeight().weight(1f - TwoPaneContentWeight).testTag(PostureTags.CONTROLS), content = controls)
                }
            }
        }
    }
}

/** The content pane's share of a flat, nearly square display. */
const val TwoPaneContentWeight = 0.58f

private val PaneDivider = Color(0x1A0A1931)

/** The hinge itself: nothing drawn or placed on it. */
@Composable
private fun HingeGap(modifier: Modifier) {
    Box(modifier.testTag(PostureTags.HINGE))
}
